import './signUp.css';
import Form from 'react-bootstrap/Form';
import FaceIcon from '../images/face-icon.png';
import { useState, useEffect, useRef } from 'react';
import Header from '../components/header';
import { postData } from '../utils/apis/api';
import Loader from '../components/loader'; // Import Loader
import CustomToaster from '../components/custom-toaster'; // Import CustomToaster
import { useNavigate, Link } from 'react-router-dom';

const SignUp = () => {
  const [step, setStep] = useState(3);
  const [emailId, setEmailId] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const otpRefs = useRef([]);
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [password, setPassword] = useState('');
  const [userName, setUserName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState('User');
  const [isLoading, setIsLoading] = useState(false); // Loader state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [resendOtpEnabled, setResendOtpEnabled] = useState(false);
  const [timer, setTimer] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (timer > 0) {
      const countdown = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
      return () => clearInterval(countdown);
    } else if (timer === 0) {
      setResendOtpEnabled(true);
    }
  }, [timer]);

  // Handle email input change
  const handleEmailChange = (event) => {
    setEmailId(event.target.value);
  };

  // Handle Send OTP
  const handleSendOtp = async () => {
    if (!emailId.includes('@') || !emailId.includes('.')) {
      setToastVariant('error');
      setToastMessage('Please enter a valid email address.');
      setShowToast(true);
      return;
    }

    setIsLoading(true); // Show Loader
    setShowToast(false); // Hide previous toast
    setToastMessage('');

    try {
      const response = await postData('/request-otp', { email: emailId });

      if (response.detail === 'OTP sent to email') {
        setToastVariant('success'); // Success variant
        setToastMessage('OTP sent to your email!');
        setShowToast(true);
        setStep(2); // Proceed to Step 2
        setResendOtpEnabled(false);
        setTimer(30); // Start timer for Resend OTP
      } else {
        throw new Error(response.detail || 'Failed to send OTP.');
      }
    } catch (error) {
      // Handle 400 Bad Request with specific message
      if (
        error.response?.status === 400 &&
        error.response?.data?.detail === 'OTP already sent, wait for a minute and click resend otp'
      ) {
        setToastVariant('warning'); // Warning variant
        setToastMessage(error.response?.data?.detail);
        setShowToast(true);
        setStep(2); // Proceed to Step 2
        setResendOtpEnabled(false);
        setTimer(30); // Start timer for Resend OTP
      } else {
        // Handle other errors
        const errorMessage =
          typeof error.response?.data?.detail === 'string'
            ? error.response.data.detail
            : typeof error.response?.data === 'object'
              ? JSON.stringify(error.response.data) // Serialize object for debugging
              : error.message || 'Failed to send OTP.';

        setToastVariant('error');
        setToastMessage(errorMessage);
        setShowToast(true);
      }
    } finally {
      setIsLoading(false); // Hide Loader
    }
  };

  // Handle Resend OTP
  const handleResendOtp = async () => {
    setIsLoading(true);

    try {
      const response = await postData('/resend-otp', { email: emailId });

      if (response.detail === 'OTP sent to email') {
        setToastVariant('success'); // Success variant
        setToastMessage('OTP sent to email');
        setShowToast(true);
        setResendOtpEnabled(false);
        setTimer(30);
      } else {
        throw new Error(response.detail || 'Failed to resend OTP.');
      }
    } catch (error) {
      setToastVariant('error');
      setToastMessage(error.response?.data?.detail || error.message || 'Failed to resend OTP.');
      setShowToast(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle OTP Change
  const handleOtpChange = (index, value) => {
    const updatedOtp = [...otp];
    updatedOtp[index] = value.slice(0, 1);
    setOtp(updatedOtp);

    if (value && index < otp.length - 1) {
      otpRefs.current[index + 1].focus();
    }
  };

  // Handle Verify OTP
  const handleVerifyOtp = async () => {
    setIsLoading(true);

    try {
      const response = await postData('/verify-otp', {
        email: emailId,
        otp: otp.join(''),
      });

      if (response.detail === 'Email verified successfully') {
        setToastVariant('success'); // Success variant
        setToastMessage('OTP verified!');
        setShowToast(true);
        setIsOtpVerified(true);
        setStep(3); // Proceed to Step 3
      } else {
        throw new Error(response.detail || 'OTP verification failed.');
      }
    } catch (error) {
      setToastVariant('error');
      setToastMessage(error.response?.data?.detail || error.message || 'OTP verification failed.');
      setShowToast(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle SignUp
  const handleSignUp = async () => {
    if (!userName || !firstName || !lastName || !password) {
      setToastVariant('error');
      setToastMessage('All fields are required.');
      setShowToast(true);
      return;
    }

    setIsLoading(true);

    const requestBody = {
      firstname: firstName,
      lastname: lastName,
      username: userName,
      email: emailId,
      password: password
    };

    try {
      const response = await postData('/signup', requestBody);

      if (response.detail === 'User Created') {
        setToastVariant('success'); // Success variant
        setToastMessage('User registered successfully!');
        setShowToast(true);

        setTimeout(() => {
          if (sessionStorage.getItem('agentName')) {
            navigate('/?agentName=' + sessionStorage.getItem('agentName'));
          }
          else {
            navigate('/');
          }
        }, 3000);
      } else {
        throw new Error(response.detail || 'User registration failed.');
      }
    } catch (error) {
      setToastVariant('error');
      setToastMessage(error.response?.data?.detail || error.message || 'User registration failed.');
      setShowToast(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Header />
      <div className="SignUpMain">
        <div className="SignUpContainer">
          <img src={FaceIcon} alt="icon" width="119" height="119" />
          <h2 className="my-2 SignUpText">Sign up to Tranquilla AI</h2>

          {/* Step 1: Email Verification */}
          {step === 1 && (
            <>
              <Form.Control
                value={emailId}
                onChange={handleEmailChange}
                className="my-3"
                type="email"
                placeholder="Email Address*"
                required
              />
              <button
                onClick={handleSendOtp}
                disabled={!emailId}
                className="ContinueButton col-12"
              >
                Send OTP (One Time Password)
              </button>
              <p className="mt-2">
                Already have a valid OTP?{' '}
                <button
                  onClick={() => setStep(2)}
                  className="btn btn-link p-0"
                  style={{ textDecoration: 'none', color: '#007bff' }}
                >
                  Click here to proceed
                </button>
              </p>
              <h6 className="mt-2">
                Click here to return to &nbsp;
                <Link to={((sessionStorage.getItem('userType') === 'user') && sessionStorage.getItem('agentName')) ? "/?agentName=" + sessionStorage.getItem('agentName') : "/"}
                >Login</Link> Page
              </h6>
            </>
          )}

          {/* Step 2: OTP Verification */}
          {step === 2 && (
            <>
              <Form.Control
                value={emailId}
                className="my-3"
                type="email"
                placeholder="Email Address*"
                disabled
              />
              <div className="otp-container">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    type="text"
                    value={digit}
                    maxLength="1"
                    className="otp-box"
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    ref={(el) => (otpRefs.current[index] = el)}
                  />
                ))}
              </div>
              {!resendOtpEnabled ? (
                <p className="mt-2 text-muted">
                  Resend OTP in {String(timer).padStart(2, '0')} seconds
                </p>
              ) : (
                <button
                  onClick={handleResendOtp}
                  className="ContinueButton col-12 mt-2"
                >
                  Resend OTP
                </button>
              )}
              <button
                onClick={handleVerifyOtp}
                disabled={otp.some((digit) => !digit)}
                className="ContinueButton col-12 mt-2"
              >
                Verify OTP
              </button>
            </>
          )}

          {/* Step 3: User Information */}
          {step === 3 && (
            <>
              <Form.Control
                value={emailId}
                className="my-3"
                type="email"
                placeholder="Email Address*"
                required
                onChange={(e) => setEmailId(e.target.value)}
              // disabled
              />
              <Form.Control
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                className="my-3"
                type="text"
                placeholder="Login Username*"
                required
              />
              <Form.Control
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="my-3"
                type="text"
                placeholder="First Name*"
                required
              />
              <Form.Control
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="my-3"
                type="text"
                placeholder="Last Name*"
                required
              />
              <Form.Control
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="my-3"
                type="password"
                placeholder="Password*"
                required
              />
              <Form.Select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="my-3"
              >
                <option value="User">User</option>
                {
                  !sessionStorage.getItem('agentName') &&
                  <option value="Admin">Admin</option>
                }
              </Form.Select>
              <button onClick={handleSignUp} className="ContinueButton col-12">
                Create User
              </button>
            </>
          )}
        </div>
      </div>

      {/* Loader */}
      {isLoading && <Loader />}

      {/* Custom Toaster */}
      {toastMessage && (
        <CustomToaster
          show={showToast}
          onClose={() => setShowToast(false)}
          message={toastMessage}
          type={toastVariant}
        />
      )}
    </>
  );
};

export default SignUp;
